import { useState, type FormEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  PageHeader,
  RowMenu,
  StatusBadge,
  Switch,
} from '../../components';
import { ApiError } from '../../lib/api';
import { useCrud } from '../../lib/useCrud';

type BirthdayTemplate = {
  id: string;
  name: string;
  subject: string | null;
  body_html: string | null;
  body_text: string | null;
  is_active: boolean;
};

type FormState = {
  name: string;
  subject: string;
  body_html: string;
  body_text: string;
  is_active: boolean;
};
const toForm = (r?: BirthdayTemplate): FormState => ({
  name: r?.name ?? '',
  subject: r?.subject ?? '',
  body_html: r?.body_html ?? '',
  body_text: r?.body_text ?? '',
  is_active: r?.is_active ?? true,
});
const toBody = (f: FormState) => ({
  name: f.name.trim(),
  subject: f.subject.trim() || null,
  body_html: f.body_html || null,
  body_text: f.body_text || null,
  is_active: f.is_active,
});

export function BirthdayTemplatesPage() {
  const crud = useCrud<BirthdayTemplate, ReturnType<typeof toBody>, Partial<ReturnType<typeof toBody>>>({
    key: 'birthday-templates',
    path: '/settings/birthday-templates',
    listQuery: 'include_inactive=true&limit=100',
  });
  const [editing, setEditing] = useState<BirthdayTemplate | 'new' | null>(null);
  const [deleting, setDeleting] = useState<BirthdayTemplate | null>(null);

  return (
    <>
      <PageHeader
        crumb="Settings"
        title="Birthday templates"
        subtitle="Templates are managed here. Channel sending (WhatsApp/email) lands in phase 2."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New template
          </Button>
        }
      />
      {crud.list.isLoading && <div className="card card-body muted">Loading…</div>}
      {crud.list.error && <div className="alert">{(crud.list.error as Error).message}</div>}
      {crud.list.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Name</th>
                <th>Subject</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {crud.list.data.data.map((row, i) => (
                <tr key={row.id}>
                  <td className="col-num">{i + 1}</td>
                  <td>{row.name}</td>
                  <td>
                    {row.subject ?? <span className="muted">—</span>}
                  </td>
                  <td>
                    <StatusBadge active={row.is_active} />
                  </td>
                  <td>
                    <RowMenu
                      items={[
                        { icon: 'edit', label: 'Edit', onClick: () => setEditing(row) },
                        'divider',
                        {
                          icon: 'trash',
                          label: 'Delete',
                          onClick: () => setDeleting(row),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          row={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            if (editing === 'new') await crud.create.mutateAsync(body);
            else await crud.update.mutateAsync({ id: editing.id, body });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete template"
        message={deleting ? `Delete "${deleting.name}"?` : null}
        destructive
        confirmLabel="Delete"
        loading={crud.remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await crud.remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function EditModal({
  row,
  onClose,
  onSubmit,
}: {
  row?: BirthdayTemplate;
  onClose: () => void;
  onSubmit: (b: ReturnType<typeof toBody>) => Promise<void>;
}) {
  const [form, setForm] = useState(() => toForm(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toBody(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit template' : 'New template'} width={640}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="bt-name"
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            autoFocus
          />
          <Input
            id="bt-subject"
            label="Subject"
            value={form.subject}
            onChange={(v) => setForm({ ...form, subject: v })}
          />
          <div>
            <label className="field-label" htmlFor="bt-text">
              Body (plain text)
            </label>
            <textarea
              id="bt-text"
              className="input"
              rows={5}
              value={form.body_text}
              onChange={(e) => setForm({ ...form, body_text: e.target.value })}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="bt-html">
              Body (HTML)
            </label>
            <textarea
              id="bt-html"
              className="input mono"
              rows={6}
              value={form.body_html}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              placeholder="<p>Happy birthday, {{first_name}}!</p>"
            />
          </div>
          <div className="row">
            <Switch
              on={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
              label="Active"
            />
            <span className="muted">Active</span>
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
